import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { 
  LogOut, Users, FileText, Mail, Eye, Trash2, 
  CheckCircle2, Clock, Archive, Loader2, Plus, Edit, X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [activeTab, setActiveTab] = useState('leads');
  const [loading, setLoading] = useState(false);
  
  // Login state
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  
  // Data state
  const [leads, setLeads] = useState([]);
  const [posts, setPosts] = useState([]);
  
  // Blog form state
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title_nl: '',
    title_en: '',
    slug: '',
    excerpt_nl: '',
    excerpt_en: '',
    content_nl: '',
    content_en: '',
    category: 'SEO',
    image_url: '',
    published: false
  });

  useEffect(() => {
    const savedToken = localStorage.getItem('kesult_admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'leads') {
        const response = await axios.get(`${API}/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLeads(response.data);
      } else if (activeTab === 'blog') {
        const response = await axios.get(`${API}/blog?published_only=false`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPosts(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, loginForm);
      const { access_token } = response.data;
      setToken(access_token);
      localStorage.setItem('kesult_admin_token', access_token);
      setIsAuthenticated(true);
      toast.success('Succesvol ingelogd!');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Ongeldige inloggegevens');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken('');
    localStorage.removeItem('kesult_admin_token');
    setIsAuthenticated(false);
  };

  const handleSetupAdmin = async () => {
    try {
      const response = await axios.post(`${API}/admin/setup`);
      toast.success(`Admin aangemaakt! Email: ${response.data.email}, Wachtwoord: ${response.data.password}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Setup failed');
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      toast.success('Status bijgewerkt');
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Weet je zeker dat je deze lead wilt verwijderen?')) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      toast.success('Lead verwijderd');
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, blogForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Artikel bijgewerkt');
      } else {
        await axios.post(`${API}/blog`, blogForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Artikel aangemaakt');
      }
      setShowBlogForm(false);
      setEditingPost(null);
      resetBlogForm();
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    } finally {
      setLoading(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Weet je zeker dat je dit artikel wilt verwijderen?')) return;
    try {
      await axios.delete(`${API}/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
      toast.success('Artikel verwijderd');
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const resetBlogForm = () => {
    setBlogForm({
      title_nl: '',
      title_en: '',
      slug: '',
      excerpt_nl: '',
      excerpt_en: '',
      content_nl: '',
      content_en: '',
      category: 'SEO',
      image_url: '',
      published: false
    });
  };

  const editPost = (post) => {
    setEditingPost(post);
    setBlogForm({
      title_nl: post.title_nl,
      title_en: post.title_en,
      slug: post.slug,
      excerpt_nl: post.excerpt_nl,
      excerpt_en: post.excerpt_en,
      content_nl: post.content_nl,
      content_en: post.content_en,
      category: post.category,
      image_url: post.image_url || '',
      published: post.published
    });
    setShowBlogForm(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-[#4286F4]';
      case 'contacted': return 'bg-[#F59E0B]';
      case 'converted': return 'bg-[#10B981]';
      case 'archived': return 'bg-[#71717A]';
      default: return 'bg-[#4286F4]';
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Toaster position="bottom-right" />
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white tracking-tighter uppercase mb-2">
              KESULT
            </h1>
            <p className="text-[#A1A1AA]">Admin Panel</p>
          </div>

          <form onSubmit={handleLogin} className="bg-[#18181B] border border-[#27272A] p-8 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                id="email"
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                required
                className="bg-[#0A0A0A] border-[#27272A] text-white"
                data-testid="admin-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Wachtwoord</Label>
              <Input
                id="password"
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                required
                className="bg-[#0A0A0A] border-[#27272A] text-white"
                data-testid="admin-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Inloggen'}
            </Button>
          </form>

          <div className="text-center mt-4">
            <button 
              onClick={handleSetupAdmin}
              className="text-sm text-[#71717A] hover:text-white"
            >
              Eerste keer? Setup admin account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#F1F0EF]" data-testid="admin-dashboard">
      <Toaster position="bottom-right" />
      
      {/* Header */}
      <header className="bg-[#0A0A0A] text-white border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <h1 className="text-2xl font-bold tracking-tighter uppercase">
              KESULT ADMIN
            </h1>
            <nav className="flex gap-4">
              <button
                onClick={() => setActiveTab('leads')}
                className={`flex items-center gap-2 px-4 py-2 font-semibold uppercase text-sm tracking-wide transition-colors ${
                  activeTab === 'leads' ? 'bg-[#4286F4]' : 'hover:bg-[#18181B]'
                }`}
                data-testid="admin-tab-leads"
              >
                <Users className="h-4 w-4" />
                Leads
              </button>
              <button
                onClick={() => setActiveTab('blog')}
                className={`flex items-center gap-2 px-4 py-2 font-semibold uppercase text-sm tracking-wide transition-colors ${
                  activeTab === 'blog' ? 'bg-[#4286F4]' : 'hover:bg-[#18181B]'
                }`}
                data-testid="admin-tab-blog"
              >
                <FileText className="h-4 w-4" />
                Blog
              </button>
            </nav>
          </div>
          <Button 
            onClick={handleLogout}
            variant="ghost"
            className="text-white hover:bg-[#18181B]"
            data-testid="admin-logout-btn"
          >
            <LogOut className="h-5 w-5 mr-2" />
            Uitloggen
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#4286F4]" />
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && !loading && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">
                LEADS ({leads.length})
              </h2>
            </div>

            <div className="bg-white border-2 border-[#F1F0EF] rounded-[2rem]">
              <table className="w-full">
                <thead className="bg-[#F1F0EF] border-b-2 border-[#E4E4E7]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold uppercase text-sm">Type</th>
                    <th className="text-left px-4 py-3 font-semibold uppercase text-sm">Naam</th>
                    <th className="text-left px-4 py-3 font-semibold uppercase text-sm">Email</th>
                    <th className="text-left px-4 py-3 font-semibold uppercase text-sm">Website</th>
                    <th className="text-left px-4 py-3 font-semibold uppercase text-sm">Status</th>
                    <th className="text-left px-4 py-3 font-semibold uppercase text-sm">Datum</th>
                    <th className="text-left px-4 py-3 font-semibold uppercase text-sm">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} className="border-b border-[#E4E4E7] hover:bg-[#F1F0EF]">
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-bold uppercase ${
                          lead.lead_type === 'quick_scan' ? 'bg-[#4286F4] text-white' : 'bg-[#FF4D00] text-white'
                        }`}>
                          {lead.lead_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold">{lead.name}</td>
                      <td className="px-4 py-3">
                        <a href={`mailto:${lead.email}`} className="text-[#4286F4] hover:underline">
                          {lead.email}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-sm text-[#71717A]">{lead.website || '-'}</td>
                      <td className="px-4 py-3">
                        <select
                          value={lead.status}
                          onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          className={`${getStatusColor(lead.status)} text-white px-2 py-1 text-xs font-bold uppercase`}
                        >
                          <option value="new">Nieuw</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                          <option value="archived">Archived</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-sm text-[#71717A]">
                        {new Date(lead.created_at).toLocaleDateString('nl-NL')}
                      </td>
                      <td className="px-4 py-3">
                        <button 
                          onClick={() => deleteLead(lead.id)}
                          className="text-[#EF4444] hover:text-[#DC2626]"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {leads.length === 0 && (
                <div className="text-center py-12 text-[#71717A]">
                  Nog geen leads.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Blog Tab */}
        {activeTab === 'blog' && !loading && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">
                BLOG ARTIKELEN ({posts.length})
              </h2>
              <Button 
                onClick={() => { setShowBlogForm(true); setEditingPost(null); resetBlogForm(); }}
                className="btn-primary"
                data-testid="admin-new-post-btn"
              >
                <Plus className="h-5 w-5 mr-2" />
                Nieuw Artikel
              </Button>
            </div>

            {/* Blog Form Modal */}
            {showBlogForm && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto border-2 border-[#F1F0EF] rounded-[2rem]">
                  <div className="sticky top-0 bg-white border-b-2 border-[#E4E4E7] px-6 py-4 flex items-center justify-between">
                    <h3 className="text-xl font-bold">
                      {editingPost ? 'Artikel Bewerken' : 'Nieuw Artikel'}
                    </h3>
                    <button onClick={() => { setShowBlogForm(false); setEditingPost(null); }}>
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  
                  <form onSubmit={handleBlogSubmit} className="p-6 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Titel (NL)</Label>
                        <Input
                          value={blogForm.title_nl}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, title_nl: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Title (EN)</Label>
                        <Input
                          value={blogForm.title_en}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, title_en: e.target.value }))}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Slug</Label>
                        <Input
                          value={blogForm.slug}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, slug: e.target.value }))}
                          required
                          placeholder="artikel-url"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Categorie</Label>
                        <Input
                          value={blogForm.category}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, category: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Afbeelding URL</Label>
                      <Input
                        value={blogForm.image_url}
                        onChange={(e) => setBlogForm(prev => ({ ...prev, image_url: e.target.value }))}
                        placeholder="https://..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Excerpt (NL)</Label>
                        <Textarea
                          value={blogForm.excerpt_nl}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, excerpt_nl: e.target.value }))}
                          required
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Excerpt (EN)</Label>
                        <Textarea
                          value={blogForm.excerpt_en}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, excerpt_en: e.target.value }))}
                          required
                          rows={3}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Content (NL) - HTML</Label>
                        <Textarea
                          value={blogForm.content_nl}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, content_nl: e.target.value }))}
                          required
                          rows={10}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Content (EN) - HTML</Label>
                        <Textarea
                          value={blogForm.content_en}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, content_en: e.target.value }))}
                          required
                          rows={10}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="published"
                        checked={blogForm.published}
                        onChange={(e) => setBlogForm(prev => ({ ...prev, published: e.target.checked }))}
                        className="w-5 h-5"
                      />
                      <Label htmlFor="published">Gepubliceerd</Label>
                    </div>

                    <div className="flex gap-4">
                      <Button type="submit" className="btn-primary" disabled={loading}>
                        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Opslaan'}
                      </Button>
                      <Button 
                        type="button" 
                        className="btn-secondary"
                        onClick={() => { setShowBlogForm(false); setEditingPost(null); }}
                      >
                        Annuleren
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Posts List */}
            <div className="bg-white border-2 border-[#F1F0EF] rounded-[2rem]">
              <table className="w-full">
                <thead className="bg-[#F1F0EF] border-b-2 border-[#E4E4E7]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold uppercase text-sm">Titel</th>
                    <th className="text-left px-4 py-3 font-semibold uppercase text-sm">Categorie</th>
                    <th className="text-left px-4 py-3 font-semibold uppercase text-sm">Status</th>
                    <th className="text-left px-4 py-3 font-semibold uppercase text-sm">Datum</th>
                    <th className="text-left px-4 py-3 font-semibold uppercase text-sm">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr key={post.id} className="border-b border-[#E4E4E7] hover:bg-[#F1F0EF]">
                      <td className="px-4 py-3 font-semibold">{post.title_nl}</td>
                      <td className="px-4 py-3">
                        <span className="badge-kesult text-[10px]">{post.category}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-bold uppercase ${
                          post.published ? 'bg-[#10B981] text-white' : 'bg-[#71717A] text-white'
                        }`}>
                          {post.published ? 'Live' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-[#71717A]">
                        {new Date(post.created_at).toLocaleDateString('nl-NL')}
                      </td>
                      <td className="px-4 py-3 flex gap-2">
                        <button 
                          onClick={() => editPost(post)}
                          className="text-[#4286F4] hover:text-[#0044CC]"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => deletePost(post.id)}
                          className="text-[#EF4444] hover:text-[#DC2626]"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {posts.length === 0 && (
                <div className="text-center py-12 text-[#71717A]">
                  Nog geen artikelen.
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
